import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Building2, TrendingUp, Calendar, Users, Shield, 
  CheckCircle, ArrowRight, ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import WaitlistSignup from '../components/WaitlistSignup';

const BecomeHostPage = () => {
  const navigate = useNavigate();
  const { user, hasHostRole, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [isUpgrading, setIsUpgrading] = useState(false);

  // If already a host, redirect — wait for auth to finish loading first
  useEffect(() => {
    if (!authLoading && hasHostRole()) {
      toast({
        title: 'Already a Host',
        description: 'You already have host access. Redirecting to dashboard.',
      });
      navigate('/host/dashboard');
    }
  }, [authLoading, hasHostRole, navigate, toast]);

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Maximize Your Earnings',
      description: 'Our profit calculator and analytics help you optimize pricing and increase revenue.'
    },
    {
      icon: Calendar,
      title: 'Unified Calendar',
      description: 'See your Ariv bookings, blocked dates and pricing in one calendar, with two-way iCal sync for Airbnb, Booking.com and Vrbo to help keep dates in step.'
    },
    {
      icon: Users,
      title: 'Service Marketplace',
      description: 'Find local cleaners, handymen and other service providers as our network grows.'
    },
    {
      icon: Shield,
      title: 'UK Compliance Ready',
      description: 'Built-in compliance tools to help you meet UK short-term rental regulations.'
    }
  ];

  const handleBecomeHost = () => {
    if (!isAuthenticated()) {
      // Redirect to signup if not logged in
      navigate('/signup');
      return;
    }
    
    setIsUpgrading(true);
    
    // Navigate to onboarding instead of directly upgrading
    setTimeout(() => {
      toast({
        title: 'Starting Host Onboarding',
        description: 'Let\'s get your host profile set up!',
      });
      setIsUpgrading(false);
      navigate('/host/onboarding');
    }, 500);
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
            </Link>
            <Button variant="outline" onClick={() => navigate(-1)}>
              Back
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F3A68] to-[#1F3A68]/80 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Building2 size={64} className="mx-auto mb-6 opacity-90" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Start a short let, the simple way.
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Own a property or start through rent to rent — Ariv is the easiest way to launch and run a UK short let, all in one app. Founding hosts pay 0% for their first 90 days.
            </p>
            {user ? (
              <Button 
                size="lg" 
                className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90 text-lg px-8 py-6"
                onClick={handleBecomeHost}
                disabled={isUpgrading}
                data-testid="upgrade-to-host-btn"
              >
                {isUpgrading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Upgrading...
                  </>
                ) : (
                  <>
                    Start Hosting Now
                    <ArrowRight className="ml-2" size={20} />
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-4">
                <p className="text-white/80">Create an account to get started</p>
                <div className="flex gap-4 justify-center">
                  <Button 
                    variant="outline" 
                    className="bg-white text-[#1F3A68] hover:bg-white/90"
                    onClick={() => navigate('/signup')}
                  >
                    Sign Up
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-white text-white hover:bg-white/10"
                    onClick={() => navigate('/login')}
                  >
                    Log In
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#1F3A68] mb-12">
            Why Host with Ariv?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#1F3A68]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon size={32} className="text-[#1F3A68]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#1F3A68] mb-12">
            How It Works
          </h2>
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {[
                { step: 1, title: 'Complete Onboarding', desc: 'Provide your details and verify your identity to meet UK compliance requirements.' },
                { step: 2, title: 'Add Your Property', desc: 'Create your first listing with photos, amenities, and pricing.' },
                { step: 3, title: 'Start Earning', desc: 'Publish your listing and start accepting bookings from guests.' }
              ].map((item) => (
                <Card key={item.step} className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#CB3B3B] text-white rounded-full flex items-center justify-center font-bold text-xl shrink-0">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                        <p className="text-gray-600">{item.desc}</p>
                      </div>
                      <ChevronRight size={24} className="text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1F3A68]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Hosting?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Be one of Ariv's first 100 founding hosts — 0% commission for your first 90 days
          </p>
          {user ? (
            <Button 
              size="lg" 
              className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
              onClick={handleBecomeHost}
              disabled={isUpgrading}
            >
              {isUpgrading ? 'Upgrading...' : 'Become a Host'}
            </Button>
          ) : (
            <Button 
              size="lg" 
              className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
              onClick={() => navigate('/signup')}
            >
              Create Account
            </Button>
          )}
        </div>
      </section>

      {/* Waitlist — for anyone not ready to create an account tonight.
          Signing up as a host is still open and is still the primary action
          above; this catches the people who would otherwise close the tab,
          and it records which side of the marketplace they are waiting for
          so we know who to call first when we open. */}
      <section className="py-16 bg-[#F4F2E9]">
        <div className="container mx-auto px-4 max-w-2xl">
          <WaitlistSignup
            defaultRole="host"
            heading="Not ready to list tonight?"
            blurb="Leave your email and we will tell you the moment we open to guests. You will keep your place among the founding hosts, and anyone who joins through your link counts as your introduction."
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F3A68]/95 text-white py-8">
        <div className="container mx-auto px-4 text-center text-white/60">
          <p>&copy; {new Date().getFullYear()} Ariv. A product of Brick &amp; Capital Ltd. All rights reserved.</p>
          <p className="text-xs mt-2 text-white/50"><strong>Brick &amp; Capital Ltd</strong> &middot; Registered in England and Wales, company number <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">16655788</a> &middot; Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX</p>
        </div>
      </footer>
    </div>
  );
};

export default BecomeHostPage;
